import { memo, type ReactNode, useState, useEffect } from 'react';
import type { GameState, Player as PlayerType } from '@bangos/shared';
import { socketManager } from '../socket-manager';
import { useT } from '../i18n';
import { PsychicView } from './PsychicView';
import { GuesserView } from './GuesserView';
import { OnboardingTooltip, hasCompletedOnboarding } from './OnboardingTooltip';
import '../styles/gameplay.css';
import '../styles/onboarding.css';

interface RevealData {
  targetPosition: number;
  guessPosition: number;
  distance: number;
  points: number;
  team: 'blue' | 'red';
  guesserId: string;
}

interface GameScreenProps {
  gameState: GameState;
  playerId: string;
  isSpectator?: boolean;
  players?: PlayerType[];
  onPlayAgain?: () => void;
  onSubmitSpectrum?: (left: string, right: string) => void;
}

export const GameScreen = memo(function GameScreen({
  gameState,
  playerId,
  isSpectator,
  players: allPlayers,
  onPlayAgain,
  onSubmitSpectrum,
}: GameScreenProps): ReactNode {
  const t = useT();
  const { phase, currentRound } = gameState;
  const currentPsychicId = currentRound?.psychicId;
  const isCurrentPlayerPsychic = currentPsychicId === playerId;

  const [clueText, setClueText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [guessPosition, setGuessPosition] = useState(50);
  const [revealData, setRevealData] = useState<RevealData | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(!hasCompletedOnboarding());
  const [spectrumLeft, setSpectrumLeft] = useState('');
  const [spectrumRight, setSpectrumRight] = useState('');
  const [spectrumFormOpen, setSpectrumFormOpen] = useState(false);

  // Check if the current player has already submitted a guess
  useEffect(() => {
    if (currentRound?.guesses) {
      const myGuess = currentRound.guesses.find((g) => g.playerId === playerId);
      if (myGuess) {
        setHasGuessed(true);
        setGuessPosition(myGuess.position);
      }
    }
  }, [currentRound?.guesses, playerId]);

  // Listen for the round_revealed event
  useEffect(() => {
    const handler = (payload: RevealData) => {
      console.log('[GameScreen] round_revealed:', payload);
      setRevealData(payload);
    };
    socketManager.on('round_revealed', handler);
    return () => {
      socketManager.off('round_revealed', handler);
    };
  }, []);

  // Reset reveal data when round changes or round_started event fires
  useEffect(() => {
    setRevealData(null);
  }, [gameState.round]);

  // Listen for round_started event to reset local state for new rounds
  useEffect(() => {
    const handler = () => {
      console.log('[GameScreen] round_started');
      setRevealData(null);
      setClueText('');
      setSubmitting(false);
      setHasGuessed(false);
      setGuessPosition(50);
    };
    socketManager.on('round_started', handler);
    return () => {
      socketManager.off('round_started', handler);
    };
  }, []);

  // Listen for game_over event
  useEffect(() => {
    const handler = () => {
      console.log('[GameScreen] game_over');
    };
    socketManager.on('game_over', handler);
    return () => {
      socketManager.off('game_over', handler);
    };
  }, []);

  const hasClue = (currentRound?.clues?.length ?? 0) > 0;
  const submittedClue = hasClue ? currentRound!.clues[0] : null;

  const handleClueSubmit = () => {
    const trimmed = clueText.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    socketManager.emit('submit_clue', { roomCode: gameState.roomCode, text: trimmed });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleClueSubmit();
    }
  };

  const handleGuessConfirm = (position: number) => {
    setHasGuessed(true);
    setGuessPosition(position);
    socketManager.emit('submit_guess', { roomCode: gameState.roomCode, position });
  };

  const getPlayerName = (pid: string): string => {
    return pid === playerId ? 'You' : pid.substring(0, 8);
  };

  const handleSpectrumSubmit = () => {
    const trimmedLeft = spectrumLeft.trim();
    const trimmedRight = spectrumRight.trim();
    if (!trimmedLeft || !trimmedRight || !onSubmitSpectrum) return;
    onSubmitSpectrum(trimmedLeft, trimmedRight);
    setSpectrumLeft('');
    setSpectrumRight('');
    setSpectrumFormOpen(false);
  };

  const showSpectrumForm = onSubmitSpectrum && (phase === 'reveal' || phase === 'gameOver');

  return (
    <div className="game-container">
      {/* Onboarding tooltip — shown on first visit or from help icon */}
      <OnboardingTooltip isOpen={showOnboarding} onClose={() => setShowOnboarding(false)} />

      {/* Help icon to re-show onboarding */}
      <button
        className="help-icon-btn"
        onClick={() => setShowOnboarding(true)}
        title={t('gameHeader.helpTitle')}
        aria-label={t('gameHeader.helpAriaLabel')}
      >
        ?
      </button>
      <div className="game-header">
        <h1 className="game-title">{t('gameHeader.title')}</h1>
        <div className="game-phase-badge">{phase}</div>
        <div className="game-info-grid">
          <div className="game-info-row">
            <span className="game-info-label">{t('gameHeader.round')}</span>
            <span className="game-info-value">
              {gameState.round + 1} / {gameState.totalRounds}
            </span>
          </div>
          <div className="game-info-row">
            <span className="game-info-label">{t('gameHeader.activeTeam')}</span>
            <span className="game-info-value">
              {gameState.activeTeam === 'blue' ? t('gameHeader.blueShort') : t('gameHeader.redShort')}
            </span>
          </div>
        </div>
      </div>

      {/* Players by team */}
      <div className="players-section">
        <h3 className="players-heading" style={{ color: '#4a90d9' }}>
          {t('gameOver.blueTeam')}
        </h3>
        <ul className="players-list">
          {gameState.teams.blue.map((pid) => (
            <li key={pid} className="player-item">
              <span className="player-name">
                {getPlayerName(pid)}
                {currentPsychicId === pid && <span className="psychic-badge">{t('psychic.psychicBadge')}</span>}
              </span>
            </li>
          ))}
        </ul>
        <h3 className="players-heading" style={{ color: '#d94a4a', marginTop: '1rem' }}>
          {t('gameOver.redTeam')}
        </h3>
        <ul className="players-list">
          {gameState.teams.red.map((pid) => (
            <li key={pid} className="player-item">
              <span className="player-name">
                {getPlayerName(pid)}
                {currentPsychicId === pid && <span className="psychic-badge">{t('psychic.psychicBadge')}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Spectators section */}
      {allPlayers && allPlayers.filter((p) => p.role === 'spectator').length > 0 && (
        <div className="spectators-section" style={{ marginTop: '1rem' }}>
          <h3 className="players-heading" style={{ color: '#888' }}>
            {t('lobby.spectators')}
          </h3>
          <ul className="players-list">
            {allPlayers
              .filter((p) => p.role === 'spectator')
              .map((p) => (
                <li key={p.id} className="player-item">
                  <span className="player-name">👁️ {p.name}</span>
                </li>
              ))}
          </ul>
        </div>
      )}

      {/* Role-specific views — conditional rendering (not CSS hiding) for security per NFR-9 */}
      {isSpectator ? (
        <div className="spectator-view">
          <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic', padding: '2rem' }}>
            {t('gameHeader.spectatorNotice')}
          </p>
        </div>
      ) : isCurrentPlayerPsychic ? (
        <PsychicView
          gameState={gameState}
          playerId={playerId}
          clueText={clueText}
          submitting={submitting}
          hasClue={hasClue}
          submittedClue={submittedClue}
          revealData={revealData}
          onPlayAgain={onPlayAgain}
          onClueTextChange={(text) => setClueText(text)}
          onClueSubmit={handleClueSubmit}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <GuesserView
          gameState={gameState}
          playerId={playerId}
          hasGuessed={hasGuessed}
          guessPosition={guessPosition}
          revealData={revealData}
          onPlayAgain={onPlayAgain}
          onGuessConfirm={handleGuessConfirm}
        />
      )}

      {/* Spectrum submission form — available during reveal and gameOver phases (between rounds) */}
      {showSpectrumForm && (
        <div className="spectrum-submission-section">
          <button
            className="btn btn--full btn--outline"
            onClick={() => setSpectrumFormOpen(!spectrumFormOpen)}
          >
            {spectrumFormOpen ? t('lobby.cancel') : t('lobby.submitSpectrum')}
          </button>

          {spectrumFormOpen && (
            <div className="spectrum-form">
              <p className="spectrum-form-description">
                {t('lobby.spectrumFormDescription')}
              </p>
              <div className="spectrum-input-row">
                <input
                  type="text"
                  className="input spectrum-input"
                  placeholder={t('lobby.spectrumLeftPlaceholder')}
                  value={spectrumLeft}
                  onChange={(e) => setSpectrumLeft(e.target.value)}
                  maxLength={50}
                />
                <span className="spectrum-separator">↔</span>
                <input
                  type="text"
                  className="input spectrum-input"
                  placeholder={t('lobby.spectrumRightPlaceholder')}
                  value={spectrumRight}
                  onChange={(e) => setSpectrumRight(e.target.value)}
                  maxLength={50}
                />
              </div>
              <button
                className="btn btn--full btn--primary"
                onClick={handleSpectrumSubmit}
                disabled={!spectrumLeft.trim() || !spectrumRight.trim()}
              >
                {t('lobby.submitSpectrumBtn')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
